import { BarChart3, Brain, Briefcase, FlaskConical, GraduationCap, Rocket, Settings, Target, Users } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logo from '../logo/lo.png';

interface DropdownItem {
  name: string;
  href: string;
  desc: string;
  icon: React.ReactNode;
}

interface NavItem {
  name: string;
  href?: string;
  dropdown?: DropdownItem[];
}

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const navigation: NavItem[] = [
    { name: 'Home', href: '/' },
    {
      name: 'Education',
      dropdown: [
        { name: 'All Courses', href: '/courses', desc: '2 Gen AI industry-ready programs', icon: <GraduationCap size={18} /> },
        { name: 'Success Stories', href: '/success-stories', desc: 'Real students. Real placements.', icon: <Rocket size={18} /> },
        { name: 'GEN AI - AIML', href: '/courses#aiml', desc: 'Machine Learning + Deep Learning', icon: <Brain size={18} /> },
        { name: 'GEN AI - Full Stack Analytics', href: '/courses#full-stack-analytics', desc: 'Excel, SQL, Power BI + Tableau', icon: <BarChart3 size={18} /> },
        { name: '50% Pay After Placement', href: '/enroll', desc: 'Learn now, pay after you get hired', icon: <Rocket size={18} /> },
        { name: 'Our Mentors', href: '/mentors', desc: 'Meet Jamuna and Rabbik', icon: <Users size={18} /> },
      ],
    },
    {
      name: 'HR Consulting',
      dropdown: [
        { name: 'Fresher Hiring', href: '/hr-consulting#fresher', desc: 'Structured campus recruitment', icon: <Target size={18} /> },
        { name: 'Technical Assessments', href: '/hr-consulting#assessment', desc: 'HackerRank evaluations', icon: <FlaskConical size={18} /> },
        { name: 'Talent Acquisition', href: '/hr-consulting#talent', desc: 'End-to-end hiring solutions', icon: <Briefcase size={18} /> },
        { name: 'HR Process Consulting', href: '/hr-consulting#process', desc: 'Optimize your HR workflows', icon: <Settings size={18} /> },
      ],
    },
    { name: 'About', href: '/about' },
    { name: 'Blog', href: '/blog' },
  ];

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setActiveDropdown(null);
  }, [location]);

  const handleContactClick = () => {
    window.scrollTo(0, 0);
    navigate('/contact');
    setIsOpen(false);
  };

  const isActive = (href?: string) => href && location.pathname === href;

  return (
    <header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        backgroundColor: isScrolled ? 'rgba(255,255,255,0.95)' : 'white',
        backdropFilter: isScrolled ? 'blur(20px)' : 'none',
        boxShadow: isScrolled ? '0 4px 30px rgba(43,34,64,0.12)' : '0 1px 0 rgba(0,0,0,0.06)',
        transition: 'all 0.3s ease',
        width: '100%',
      }}
    >
      <nav ref={dropdownRef} style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '72px' }}>
          {/* Logo */}
          <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', flexShrink: 0 }}>
            <img
              src={logo}
              alt="Vista Tech"
              style={{ height: '64px', width: 'auto', objectFit: 'contain' }}
            />
          </Link>

          {/* Desktop Navigation */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            {navigation.map((item) => (
              item.dropdown ? (
                <div key={item.name} style={{ position: 'relative' }}>
                  <button
                    onClick={() => setActiveDropdown(activeDropdown === item.name ? null : item.name)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '8px 14px',
                      borderRadius: '8px',
                      border: 'none',
                      background: activeDropdown === item.name ? 'rgba(43,34,64,0.08)' : 'transparent',
                      color: activeDropdown === item.name ? '#2b2240' : '#4b5563',
                      fontWeight: activeDropdown === item.name ? '600' : '500',
                      fontSize: '0.9rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      fontFamily: 'Poppins, sans-serif',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#2b2240'; e.currentTarget.style.background = 'rgba(43,34,64,0.06)'; }}
                    onMouseLeave={(e) => { if (activeDropdown !== item.name) { e.currentTarget.style.color = '#4b5563'; e.currentTarget.style.background = 'transparent'; } }}
                  >
                    {item.name}
                    <svg
                      style={{ width: '14px', height: '14px', transition: 'transform 0.2s', transform: activeDropdown === item.name ? 'rotate(180deg)' : 'rotate(0deg)' }}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {activeDropdown === item.name && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 8px)',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: 'white',
                        borderRadius: '16px',
                        boxShadow: '0 20px 60px rgba(43,34,64,0.18)',
                        border: '1px solid rgba(43,34,64,0.08)',
                        padding: '8px',
                        minWidth: item.name === 'Education' ? '480px' : '340px',
                        display: 'grid',
                        gridTemplateColumns: item.name === 'Education' ? '1fr 1fr' : '1fr',
                        gap: '4px',
                        zIndex: 1001,
                        animation: 'dropdownFadeIn 0.15s ease',
                      }}
                    >
                      {item.dropdown.map((d) => (
                        <Link
                          key={d.name}
                          to={d.href}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '10px 14px',
                            borderRadius: '10px',
                            textDecoration: 'none',
                            transition: 'background 0.15s',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(43,34,64,0.05)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                        >
                          <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(43,34,64,0.07)', color: '#4e3a69' }}>{d.icon}</span>
                          <div>
                            <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#2b2240', lineHeight: 1.3 }}>{d.name}</div>
                            <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '1px' }}>{d.desc}</div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={item.name}
                  to={item.href!}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '8px',
                    color: isActive(item.href) ? '#2b2240' : '#4b5563',
                    fontWeight: isActive(item.href) ? '600' : '500',
                    fontSize: '0.9rem',
                    textDecoration: 'none',
                    transition: 'all 0.2s',
                    fontFamily: 'Poppins, sans-serif',
                    background: isActive(item.href) ? 'rgba(43,34,64,0.08)' : 'transparent',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = '#2b2240'; e.currentTarget.style.background = 'rgba(43,34,64,0.06)'; }}
                  onMouseLeave={(e) => { if (!isActive(item.href)) { e.currentTarget.style.color = '#4b5563'; e.currentTarget.style.background = 'transparent'; } }}
                >
                  {item.name}
                </Link>
              )
            ))}

            <button
              onClick={handleContactClick}
              style={{
                marginLeft: '8px',
                padding: '10px 20px',
                background: 'linear-gradient(135deg, #2b2240, #4e3a69)',
                color: 'white',
                borderRadius: '10px',
                border: 'none',
                fontWeight: '600',
                fontSize: '0.875rem',
                cursor: 'pointer',
                transition: 'all 0.2s',
                fontFamily: 'Poppins, sans-serif',
                boxShadow: '0 4px 14px rgba(43,34,64,0.3)',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(43,34,64,0.4)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(43,34,64,0.3)'; }}
            >
              Contact Us
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden"
            style={{
              padding: '8px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: 'transparent',
              cursor: 'pointer',
              display: 'none',
            }}
          >
            {isOpen ? (
              <svg style={{ width: '24px', height: '24px', color: '#2b2240' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg style={{ width: '24px', height: '24px', color: '#2b2240' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div style={{
            backgroundColor: 'white',
            borderTop: '1px solid rgba(43,34,64,0.08)',
            padding: '12px 0 20px',
            maxHeight: '80vh',
            overflowY: 'auto',
          }}>
            {navigation.map((item) => (
              <div key={item.name}>
                {item.dropdown ? (
                  <>
                    <button
                      onClick={() => setMobileExpanded(mobileExpanded === item.name ? null : item.name)}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '12px 20px',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#2b2240',
                        fontWeight: '600',
                        fontSize: '0.95rem',
                        fontFamily: 'Poppins, sans-serif',
                      }}
                    >
                      {item.name}
                      <svg
                        style={{ width: '16px', height: '16px', transition: 'transform 0.2s', transform: mobileExpanded === item.name ? 'rotate(180deg)' : 'none' }}
                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {mobileExpanded === item.name && (
                      <div style={{ background: 'rgba(43,34,64,0.03)', padding: '4px 0' }}>
                        {item.dropdown.map((d) => (
                          <Link
                            key={d.name}
                            to={d.href}
                            onClick={() => setIsOpen(false)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px',
                              padding: '10px 32px',
                              textDecoration: 'none',
                              color: '#4b5563',
                              fontSize: '0.875rem',
                            }}
                          >
                            <span style={{ display: 'flex', alignItems: 'center', color: '#4e3a69' }}>{d.icon}</span>
                            <span>{d.name}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={item.href!}
                    onClick={() => setIsOpen(false)}
                    style={{
                      display: 'block',
                      padding: '12px 20px',
                      color: isActive(item.href) ? '#2b2240' : '#4b5563',
                      fontWeight: isActive(item.href) ? '600' : '400',
                      fontSize: '0.95rem',
                      textDecoration: 'none',
                      fontFamily: 'Poppins, sans-serif',
                    }}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
            <div style={{ padding: '12px 20px 0' }}>
              <button
                onClick={handleContactClick}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'linear-gradient(135deg, #2b2240, #4e3a69)',
                  color: 'white',
                  borderRadius: '10px',
                  border: 'none',
                  fontWeight: '600',
                  fontSize: '0.95rem',
                  cursor: 'pointer',
                  fontFamily: 'Poppins, sans-serif',
                }}
              >
                Contact Us
              </button>
            </div>
          </div>
        )}
      </nav>

      <style>{`
        @media (max-width: 1024px) {
          nav > div > div:nth-child(2) { display: none !important; }
          nav > div > button:last-child { display: block !important; }
        }
        @keyframes dropdownFadeIn {
          from { opacity: 0; transform: translateX(-50%) translateY(-8px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
    </header>
  );
};

export default Header;
